import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TurnstileWidget from "../shared/TurnstileWidget.jsx";
import { errorAria } from "../../utils/formA11y.js";
import { API_BASE_URL } from "../../utils/api.js";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const DATENSCHUTZ_PATH = { de: "datenschutz", en: "privacy" };
const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID;

const donationSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  street: z.string().min(1),
  postalCode: z.string().min(1),
  city: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  company: z.string().optional(),
  amount: z.coerce.number().positive(),
  donationType: z.enum(["one_time", "recurring"]),
  paymentMethod: z.enum(["paypal", "bank_transfer"]),
  receiptRequested: z.boolean().default(true),
  message: z.string().optional(),
  gdprConsent: z.boolean().refine((v) => v === true),
  honeypot: z.string().max(0),
});

const fieldStyle = {
  fontFamily: "Inter, sans-serif",
  fontSize: "15px",
  color: "var(--color-ink)",
  background: "var(--color-cream)",
  border: "1px solid rgba(10,9,7,0.2)",
  borderRadius: "8px",
  padding: "12px 14px",
  width: "100%",
};

const labelStyle = { display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px", color: "rgba(10,9,7,0.65)" };

const rowStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" };

const errorStyle = { fontSize: "12px", color: "#b3261e" };

function Field({ label, error, children }) {
  return (
    <label style={labelStyle}>
      {label}
      {children}
      {error && (
        <span id={error.id} role="alert" style={errorStyle}>
          {error.message}
        </span>
      )}
    </label>
  );
}

function RadioPill({ name, value, checked, label, register }) {
  return (
    <label className={`tl-radio-pill ${checked ? "tl-pill tl-pill-dark" : "tl-pill tl-pill-ghost-dark"}`}>
      <input type="radio" value={value} {...register(name)} />
      <span>{label}</span>
    </label>
  );
}

// Amount presets aren't part of the submitted schema — "custom" just
// switches the number input on, the presets write straight into RHF's
// "amount" field via setValue, same UI language as RadioPill above but
// locally controlled instead of register()-bound.
function AmountPresetPill({ checked, label, onSelect }) {
  return (
    <label className={`tl-radio-pill ${checked ? "tl-pill tl-pill-primary" : "tl-pill tl-pill-ghost-dark"}`}>
      <input type="radio" name="amountPreset" checked={checked} onChange={onSelect} />
      <span>{label}</span>
    </label>
  );
}

const AMOUNT_PRESETS = ["5", "10", "50"];

/**
 * §3/§7: hidden projectSlug/projectTitle, PayPal Buttons SDK, bank-transfer
 * fallback. Submits to POST /api/donations first (creates the `pending`
 * record) — the returned donation id becomes the PayPal order's
 * `custom_id`, which donations-paypal-webhook uses to find the record back.
 * Confirmation itself always comes from the webhook, never from the
 * client — this component only reports what the donor sees.
 */
export default function DonationForm({ lang, projectSlug, projectTitle }) {
  const { t } = useTranslation();
  const [turnstileToken, setTurnstileToken] = useState("");
  const [step, setStep] = useState("form"); // form | paypal | done | error
  const [donation, setDonation] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [amountPreset, setAmountPreset] = useState("10");

  const handleVerify = useCallback((token) => setTurnstileToken(token), []);
  const handleExpire = useCallback(() => setTurnstileToken(""), []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(donationSchema),
    defaultValues: { donationType: "one_time", paymentMethod: "paypal", receiptRequested: true, amount: 10 },
  });

  const paymentMethod = watch("paymentMethod");
  const donationType = watch("donationType");
  const amount = watch("amount");

  function selectAmountPreset(preset) {
    setAmountPreset(preset);
    if (preset !== "custom") {
      setValue("amount", Number(preset), { shouldValidate: true });
    }
  }

  async function onSubmit(values) {
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/donations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          projectSlug,
          projectTitle,
          language: lang,
          gdprConsent: { given: values.gdprConsent },
          turnstileToken,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setDonation({ ...data, amount: values.amount });
      setStep(values.paymentMethod === "paypal" ? "paypal" : "done");
    } catch (err) {
      setSubmitError(err.message);
      setStep("error");
    }
  }

  if (step === "done") {
    return (
      <p role="status" style={{ fontSize: "15px", lineHeight: 1.6, color: "var(--color-ink)" }}>
        {t("spenden.bankTransferThanks", { reference: donation.paymentReference })}
      </p>
    );
  }

  if (step === "error") {
    return (
      <p role="alert" style={{ fontSize: "15px", color: "#b3261e" }}>
        {t("spenden.submitError")}
      </p>
    );
  }

  if (step === "paypal") {
    if (!PAYPAL_CLIENT_ID) {
      return (
        <p role="alert" style={{ fontSize: "15px", color: "#b3261e" }}>
          {t("spenden.paypalNotConfigured")}
        </p>
      );
    }
    return (
      <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR" }}>
        <PayPalButtons
          createOrder={async () => {
            // Order is created server-side from the stored donation.amount,
            // not from client state — see api/donations-create-paypal-order.
            const res = await fetch(`${API_BASE_URL}/api/donations/${donation.id}/paypal-order`, { method: "POST" });
            if (!res.ok) throw new Error(`order creation failed: ${res.status}`);
            const { orderId } = await res.json();
            return orderId;
          }}
          onApprove={(_data, actions) => actions.order.capture().then(() => setStep("done"))}
          onError={() => setStep("error")}
        />
      </PayPalScriptProvider>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <input type="text" tabIndex={-1} autoComplete="off" {...register("honeypot")} style={{ display: "none" }} />

      <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>
        {projectTitle}
      </span>

      <div style={rowStyle}>
        <Field label={t("spenden.form.firstName")} error={errors.firstName && { id: "firstName-error", message: t("spenden.fieldRequired") }}>
          <input type="text" style={fieldStyle} {...errorAria(errors, "firstName")} {...register("firstName")} />
        </Field>
        <Field label={t("spenden.form.lastName")} error={errors.lastName && { id: "lastName-error", message: t("spenden.fieldRequired") }}>
          <input type="text" style={fieldStyle} {...errorAria(errors, "lastName")} {...register("lastName")} />
        </Field>
      </div>

      <Field label={t("spenden.form.street")} error={errors.street && { id: "street-error", message: t("spenden.fieldRequired") }}>
        <input type="text" style={fieldStyle} {...errorAria(errors, "street")} {...register("street")} />
      </Field>

      <div style={rowStyle}>
        <Field label={t("spenden.form.postalCode")} error={errors.postalCode && { id: "postalCode-error", message: t("spenden.fieldRequired") }}>
          <input type="text" style={fieldStyle} {...errorAria(errors, "postalCode")} {...register("postalCode")} />
        </Field>
        <Field label={t("spenden.form.city")} error={errors.city && { id: "city-error", message: t("spenden.fieldRequired") }}>
          <input type="text" style={fieldStyle} {...errorAria(errors, "city")} {...register("city")} />
        </Field>
      </div>

      <Field label={t("spenden.form.email")} error={errors.email && { id: "email-error", message: t("spenden.invalidEmail") }}>
        <input type="email" style={fieldStyle} {...errorAria(errors, "email")} {...register("email")} />
      </Field>

      <Field label={t("spenden.form.phone")}>
        <input type="tel" style={fieldStyle} {...register("phone")} />
      </Field>

      <Field label={t("spenden.form.company")}>
        <input type="text" style={fieldStyle} {...register("company")} />
      </Field>

      <fieldset style={{ border: "1px solid rgba(10,9,7,0.14)", borderRadius: "8px", padding: "12px 14px" }}>
        <legend style={{ fontSize: "13px", color: "rgba(10,9,7,0.65)", padding: "0 4px" }}>{t("spenden.form.amount")}</legend>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
          {AMOUNT_PRESETS.map((preset) => (
            <AmountPresetPill
              key={preset}
              checked={amountPreset === preset}
              label={`${preset} €`}
              onSelect={() => selectAmountPreset(preset)}
            />
          ))}
          <AmountPresetPill checked={amountPreset === "custom"} label={t("spenden.form.customAmount")} onSelect={() => selectAmountPreset("custom")} />
        </div>
        {amountPreset === "custom" && (
          <input
            type="number"
            step="0.01"
            min="1"
            style={{ ...fieldStyle, marginTop: "12px", maxWidth: "200px", marginLeft: "auto", marginRight: "auto" }}
            {...errorAria(errors, "amount")}
            {...register("amount")}
          />
        )}
        {errors.amount && (
          <span id="amount-error" role="alert" style={{ ...errorStyle, display: "block", marginTop: "8px", textAlign: "center" }}>
            {t("spenden.fieldRequired")}
          </span>
        )}
      </fieldset>

      <fieldset style={{ border: "1px solid rgba(10,9,7,0.14)", borderRadius: "8px", padding: "12px 14px" }}>
        <legend style={{ fontSize: "13px", color: "rgba(10,9,7,0.65)", padding: "0 4px" }}>{t("spenden.form.donationTypeLegend")}</legend>
        <div style={{ display: "flex", gap: "10px" }}>
          <RadioPill name="donationType" value="one_time" checked={donationType === "one_time"} label={t("spenden.form.oneTime")} register={register} />
          <RadioPill name="donationType" value="recurring" checked={donationType === "recurring"} label={t("spenden.form.recurring")} register={register} />
        </div>
      </fieldset>

      <fieldset style={{ border: "1px solid rgba(10,9,7,0.14)", borderRadius: "8px", padding: "12px 14px" }}>
        <legend style={{ fontSize: "13px", color: "rgba(10,9,7,0.65)", padding: "0 4px" }}>{t("spenden.form.paymentMethodLegend")}</legend>
        <div style={{ display: "flex", gap: "10px" }}>
          <RadioPill name="paymentMethod" value="paypal" checked={paymentMethod === "paypal"} label={t("spenden.form.paypal")} register={register} />
          <RadioPill name="paymentMethod" value="bank_transfer" checked={paymentMethod === "bank_transfer"} label={t("spenden.form.bankTransfer")} register={register} />
        </div>
      </fieldset>

      <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "13px", color: "rgba(10,9,7,0.7)" }}>
        <input type="checkbox" defaultChecked style={{ marginTop: "2px", accentColor: "var(--color-gold)" }} {...register("receiptRequested")} />
        {t("spenden.form.receiptRequested")}
      </label>

      <Field label={t("spenden.form.message")}>
        <textarea style={{ ...fieldStyle, minHeight: "80px", resize: "vertical" }} {...register("message")} />
      </Field>

      <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "12px", color: "rgba(10,9,7,0.6)" }}>
        <input
          type="checkbox"
          style={{ marginTop: "2px", accentColor: "var(--color-gold)" }}
          {...errorAria(errors, "gdprConsent")}
          {...register("gdprConsent")}
        />
        <span>
          {t("spenden.form.gdprConsentPrefix")}{" "}
          <Link to={`/${lang}/${DATENSCHUTZ_PATH[lang]}`} target="_blank" rel="noopener" style={{ color: "var(--color-ink)", textDecoration: "underline" }}>
            {t("spenden.form.gdprConsentLinkText")}
          </Link>
          {t("spenden.form.gdprConsentSuffix")}
        </span>
      </label>
      {errors.gdprConsent && (
        <span id="gdprConsent-error" role="alert" style={errorStyle}>
          {t("spenden.consentRequired")}
        </span>
      )}

      {TURNSTILE_SITE_KEY && (
        <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onVerify={handleVerify} onExpire={handleExpire} />
      )}

      {submitError && (
        <p role="alert" style={errorStyle}>
          {t("spenden.submitError")}
        </p>
      )}

      <button
        type="submit"
        className="tl-pill tl-pill-dark"
        style={{ width: "100%", padding: "15px 0", fontSize: "15px", opacity: isSubmitting ? 0.6 : 1 }}
        disabled={isSubmitting || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)}
      >
        {paymentMethod === "paypal" ? t("spenden.form.submitPaypal") : t("spenden.form.submitBankTransfer")}
        {amount ? ` (${amount}€)` : ""}
      </button>
    </form>
  );
}
